import { createContext, useContext, useEffect, useState, useCallback } from "react";
import {
  authMe,
  authLogin,
  authLogout,
  authRegister,
  setAuthTokens,
  clearAuthTokens,
} from "@/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking; false = signed out; object = signed in
  const [user, setUser] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const me = await authMe();
      setUser(me);
      return me;
    } catch {
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function login(email, password) {
    const u = await authLogin({ email, password });
    if (u?.access_token) {
      setAuthTokens({
        access_token: u.access_token,
        refresh_token: u.refresh_token,
      });
    }
    setUser(u);
    return u;
  }

  async function register(email, password, name) {
    const u = await authRegister({ email, password, name });
    if (u?.access_token) {
      setAuthTokens({
        access_token: u.access_token,
        refresh_token: u.refresh_token,
      });
    }
    setUser(u);
    return u;
  }

  async function logout() {
    try {
      await authLogout();
    } catch {
      /* ignore */
    }
    clearAuthTokens();
    setUser(false);
  }

  return (
    <AuthContext.Provider value={{ user, login, register, logout, refresh, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}

export function hasActiveAccess(user) {
  // Mirrors backend shared.is_active_subscriber: admins always have access;
  // trialing users until trial_ends_at; active/canceled/past_due until
  // current_period_end (canceling a payment keeps access until it runs out).
  if (!user || typeof user !== "object") return false;
  if (user.role === "admin") return true;
  const future = (v) => {
    if (!v) return false;
    const t = new Date(v).getTime();
    return !Number.isNaN(t) && t > Date.now();
  };
  const status = user.subscription_status;
  if (status === "trialing") return future(user.trial_ends_at);
  if (status === "active" || status === "canceled" || status === "past_due")
    return future(user.current_period_end);
  return false;
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
